import { CreateBusinessPayload } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"

import {
  bulkCreateBusinesses,
  BulkCreateBusinessesPayload,
  bulkDestroyBusinesses,
  bulkUpdateBusinesses,
  BulkUpdateBusinessesPayload,
  createBusiness,
  mergeBusiness,
  MergeBusinessResponse,
  updateBusiness,
} from "./api"

interface CreateBusinessMutationParams {
  business: CreateBusinessPayload
  overrideBookId?: string
}

export function useCreateBusiness() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ business, overrideBookId }: CreateBusinessMutationParams) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return createBusiness(context, business, overrideBookId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businesses"] })
    },
  })
}

export function useBulkUpdateBusinesses() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkUpdateBusinessesPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkUpdateBusinesses(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businesses"] })
    },
  })
}

export interface MergeBusinessesParams {
  source_business_id: string
  target_business_id: string
}

export function useMergeBusinesses() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<MergeBusinessResponse, Error, MergeBusinessesParams>({
    mutationFn: async ({ source_business_id, target_business_id }) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return mergeBusiness(context, source_business_id, target_business_id)
    },
    meta: { errorToast: { fallback: "Failed to merge businesses" } },
    onSuccess: (_data, { source_business_id }) => {
      queryClient.removeQueries({ queryKey: ["business", source_business_id] })
      queryClient.invalidateQueries({ queryKey: ["businesses"] })
      queryClient.invalidateQueries({ queryKey: ["business"] })
    },
  })
}

export function useBulkCreateBusinesses() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkCreateBusinessesPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkCreateBusinesses(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businesses"] })
      queryClient.invalidateQueries({ queryKey: ["lists"] })
      toast.success("Business import jobs queued successfully")
    },
  })
}

export function useBulkDestroyBusinesses() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (businessIds: string[]) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkDestroyBusinesses(context, businessIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businesses"] })
    },
  })
}

export function useUploadBusinessProfilePicture(
  businessId: string,
  options?: {
    onSuccess?: () => void
  }
) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return updateBusiness(context, businessId, { profile_picture: file })
    },
    onSuccess: () => {
      toast.success("Profile picture uploaded")
      queryClient.invalidateQueries({ queryKey: ["business", businessId] })
      queryClient.invalidateQueries({ queryKey: ["businesses"] })
      options?.onSuccess?.()
    },
  })
}

export function useRemoveBusinessProfilePicture(businessId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return updateBusiness(context, businessId, { profile_picture: null })
    },
    onSuccess: () => {
      toast.success("Profile picture removed")
      queryClient.invalidateQueries({ queryKey: ["business", businessId] })
      queryClient.invalidateQueries({ queryKey: ["businesses"] })
    },
  })
}

export function useDestroyBusiness() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (businessId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return apiContextFetch(context, `businesses/${businessId}`, {
        method: "DELETE",
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businesses"] })
    },
  })
}
